// Metroid Prime: Origins .NUT save codec (INI hex wrapper + GameMaker tagged binary).

export function bytesToLatin1(bytes) {
  let s = ""
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return s
}

export function latin1ToBytes(str) {
  const b = new Uint8Array(str.length)
  for (let i = 0; i < str.length; i++) b[i] = str.charCodeAt(i) & 0xff
  return b
}

export function hexToBytes(hex) {
  if (hex.length % 2 !== 0) {
    throw new Error("Hex payload has an odd length — file looks truncated or corrupted.")
  }
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16)
  return out
}

export function bytesToHex(bytes) {
  let s = ""
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, "0")
  return s.toUpperCase()
}

function Reader(bytes) {
  this.bytes = bytes
  this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  this.pos = 0
}
Reader.prototype.u32 = function () {
  const v = this.view.getUint32(this.pos, true)
  this.pos += 4
  return v
}
Reader.prototype.f64 = function () {
  const v = this.view.getFloat64(this.pos, true)
  this.pos += 8
  return v
}
Reader.prototype.u64 = function () {
  const v = this.view.getBigUint64(this.pos, true)
  this.pos += 8
  return v
}
Reader.prototype.take = function (n) {
  if (this.pos + n > this.bytes.length) {
    throw new Error(`Ran off the end of the file while reading ${n} bytes at offset ${this.pos}.`)
  }
  const b = this.bytes.slice(this.pos, this.pos + n)
  this.pos += n
  return b
}

function readValue(r) {
  const t = r.u32()
  if (t === 0) return { type: 0, value: r.f64() }
  if (t === 1) {
    const n = r.u32()
    return { type: 1, value: bytesToLatin1(r.take(n)) }
  }
  if (t === 2) {
    const n = r.u32()
    const arr = []
    for (let i = 0; i < n; i++) arr.push(readValue(r))
    return { type: 2, value: arr }
  }
  if (t === 15) return { type: 15, value: r.u64() }
  throw new Error(
    `Unrecognized value type "${t}" at byte offset ${r.pos - 4}. This save may use a format variant this tool doesn't know yet.`,
  )
}

export function decode(bytes) {
  const r = new Reader(bytes)
  const magic = r.u32()
  const count = r.u32()
  const fields = []
  for (let i = 0; i < count; i++) {
    const flag = r.u32()
    const nameLen = r.u32()
    const name = bytesToLatin1(r.take(nameLen))
    const v = readValue(r)
    fields.push({ flag, name, type: v.type, value: v.value })
  }
  return { magic, fields }
}

function Writer() {
  this.chunks = []
  this.length = 0
}
Writer.prototype.push = function (b) {
  this.chunks.push(b)
  this.length += b.length
}
Writer.prototype.u32 = function (v) {
  const b = new Uint8Array(4)
  new DataView(b.buffer).setUint32(0, v >>> 0, true)
  this.push(b)
}
Writer.prototype.f64 = function (v) {
  const b = new Uint8Array(8)
  new DataView(b.buffer).setFloat64(0, v, true)
  this.push(b)
}
Writer.prototype.u64 = function (v) {
  const b = new Uint8Array(8)
  const big = typeof v === "bigint" ? v : BigInt(Math.round(v))
  new DataView(b.buffer).setBigUint64(0, big, true)
  this.push(b)
}
Writer.prototype.raw = function (bytes) {
  this.push(bytes)
}
Writer.prototype.toBytes = function () {
  const out = new Uint8Array(this.length)
  let o = 0
  for (const c of this.chunks) {
    out.set(c, o)
    o += c.length
  }
  return out
}

function writeValue(w, node) {
  w.u32(node.type)
  if (node.type === 0) {
    w.f64(node.value)
  } else if (node.type === 1) {
    const b = latin1ToBytes(node.value)
    w.u32(b.length)
    w.raw(b)
  } else if (node.type === 2) {
    w.u32(node.value.length)
    for (const child of node.value) writeValue(w, child)
  } else if (node.type === 15) {
    w.u64(node.value)
  } else {
    throw new Error("Unknown type on write: " + node.type)
  }
}

export function encode(doc) {
  const w = new Writer()
  w.u32(doc.magic)
  w.u32(doc.fields.length)
  for (const f of doc.fields) {
    w.u32(f.flag)
    const nb = latin1ToBytes(f.name)
    w.u32(nb.length)
    w.raw(nb)
    writeValue(w, f)
  }
  return w.toBytes()
}

const INI_RE = /0="([0-9A-Fa-f]+)"/

export async function loadFile(file) {
  const text = await file.text()
  const m = text.match(INI_RE)
  if (!m) {
    throw new Error('No [SaveData] 0="…" entry found — this doesn\'t look like a Metroid Prime: Origins .NUT save file.')
  }
  const hex = m[1]
  const hexStart = m.index + m[0].indexOf(m[1])
  const bytes = hexToBytes(hex)
  const doc = decode(bytes)
  return { text, hexStart, hexLength: hex.length, doc, originalSize: bytes.length }
}

export function rebuildText(originalText, hexStart, hexLength, doc) {
  const newHex = bytesToHex(encode(doc))
  return originalText.slice(0, hexStart) + newHex + originalText.slice(hexStart + hexLength)
}

export function formatNumber(v) {
  if (typeof v === "bigint") return v.toString()
  if (Number.isInteger(v)) return String(v)
  return v.toFixed(6).replace(/0+$/, "").replace(/\.$/, "")
}

/**
 * Infer which MPO .NUT this is from decoded field names (not the filename).
 * Extras and Options have unique keys. Normal vs Fusion Mode *slot* saves share
 * the same schema — Fusion progress is a subset — so those are both "slot".
 *
 * @returns {{ id: string, label: string }}
 */
export function detectSaveKind(doc) {
  const names = new Set((doc?.fields || []).map((f) => f.name))
  const has = (name) => names.has(name)

  // Metroid Extras.NUT — Fusion Mode unlock + ending cards / costume.
  if (has("Fusion Mode") || (has("New Extras") && has("Ending Cards"))) {
    return { id: "extras", label: "Metroid Extras" }
  }

  // Metroid Options.NUT — display / audio / controls.
  if (has("Fullscreen") && has("Window Size")) {
    return { id: "options", label: "Metroid Options" }
  }

  // Samus A / Fusion A (and similar) — same field schema; can't tell campaign
  // from contents alone (a short Fusion run looks like an early normal save).
  if (has("Space Jump Boots") || has("Morph Ball Bomb") || has("Missiles Max")) {
    return { id: "slot", label: "Slot save (normal or Fusion Mode)" }
  }

  return { id: "unknown", label: "Unknown .NUT" }
}
